import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Handshake, Building2, Search, Inbox, RefreshCw, CalendarClock, User,
  FileSignature, AlertTriangle, Plus, X, Building,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  contractsApi, crmApi, workflowApi, type CrmClient, type Lead, type Opportunity,
} from '../services/api';
import type { ContractState } from '../types';
import { useAppStore } from '../store/useAppStore';
import { useCrmSellerStore } from '../store/useCrmSellerStore';
import { useProjectStore } from '../store/useProjectStore';
import SellingAs from '../components/crm/SellingAs';

const SOURCES = ['inbound', 'outbound', 'referral', 'expansion', 'rfp'];
const FIELD = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';
const LABEL = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

interface Stage {
  id: string;
  label: string;
  color?: string;
  order?: number;
}

/**
 * Commercial pipeline.
 *
 * Stages come from the workflow bound to the `opportunity` type, deliberately
 * separate from the lead pipeline: a lead asks "is there anything here?", an
 * opportunity asks "will it close, when, and for how much?". Sharing stages
 * would make both wrong.
 */

/** Minor units to a display string.
 *
 *  All arithmetic upstream is integer minor units; this is the only place a
 *  value becomes a string, and nothing downstream does arithmetic on it. Doing
 *  it the other way round is how currency bugs get baked in.
 */
function money(minor: number, currency: string): string {
  const value = (minor || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: currency || 'USD',
      maximumFractionDigits: value >= 1000 ? 0 : 2,
    }).format(value);
  } catch {
    // An unknown ISO code must not blank the whole page.
    return `${currency || ''} ${value.toFixed(2)}`.trim();
  }
}

/** Contract state for a deal, and the absence of one where it matters.
 *
 *  A won deal with no executed contract is the case the provisioning gate is
 *  about to start refusing, so it is called out rather than left blank —
 *  otherwise the first sign is someone asking why access never appeared.
 *  Anywhere else, no contract is simply normal and says nothing.
 */
function ContractBadge({ state, won }: { state?: ContractState; won: boolean }) {
  if (!state) {
    if (!won) return null;
    return (
      <span
        className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 flex items-center gap-1"
        title="Won with no contract raised. Provisioning is withheld once the contract gate is enforced."
      >
        <AlertTriangle size={10} /> no contract
      </span>
    );
  }

  const executed = state.status === 'executed';
  const dead = state.status === 'void';
  const tone = executed
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : dead
    ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
    : won
    // Raised but not signed, on a deal already called won. Same consequence as
    // no contract at all, so it reads the same way.
    ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';

  return (
    <span
      className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${tone}`}
      title={`${state.key} — ${state.status}`
        + (state.executionDate ? `, executed ${state.executionDate}` : '')}
    >
      <FileSignature size={10} /> {state.status}
    </span>
  );
}

export default function OpportunitiesPage() {
  const { setDetailItemId } = useAppStore();
  const sellerOrgId = useCrmSellerStore((s) => s.sellerOrgId);
  const nameOf = useCrmSellerStore((s) => s.nameOf);
  const loadSellers = useCrmSellerStore((s) => s.loadSellers);
  const [creating, setCreating] = useState(false);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  // Partner names for the rows. Every account, once, rather than per card.
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [pipelineValue, setPipelineValue] = useState(0);
  const [weightedValue, setWeightedValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The session's company: its deals, plus any filed before sellers existed.
      const data = await crmApi.listOpportunities({ sellerOrgId });
      setOpps(data.opportunities || []);
      setSources(data.sources || []);
      setPipelineValue(data.pipelineValueMinor || 0);
      setWeightedValue(data.weightedValueMinor || 0);

      // Where a new deal goes: the CRM project the deals already live in, or
      // failing that the one the leads do.
      let known = data.projectIds || [];
      if (!known.length) {
        try { known = (await crmApi.listLeads()).projectIds || []; } catch { /* asked on create */ }
      }
      setProjectIds(known);

      const projectId = known[0];
      try {
        const wf = await workflowApi.resolve({ projectId, workItemType: 'opportunity' });
        setStages(((wf?.states || []) as Stage[]).slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      } catch {
        setStages([]);
      }
    } catch (err: any) {
      toast.error(err.message || 'Could not load opportunities');
    } finally {
      setLoading(false);
    }
  }, [sellerOrgId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadSellers();
    crmApi.listClients(undefined, undefined, { includeArchived: true })
      .then((r) => setOrgNames(Object.fromEntries(
        (r.clients || []).filter((c) => c.orgId).map((c) => [c.orgId!.toUpperCase(), c.name]))))
      .catch(() => setOrgNames({}));
  }, [loadSellers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return opps.filter((o) => {
      if (source && o.source !== source) return false;
      if (!q) return true;
      return [o.title, o.account, o.contactName, o.contactEmail]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [opps, search, source]);

  const grouped = useMemo(() => {
    const buckets = new Map<string, Opportunity[]>();
    stages.forEach((s) => buckets.set(s.id, []));
    filtered.forEach((o) => {
      const key = o.status || 'qualification';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(o);
    });
    return [...buckets.entries()].filter(([, v]) => v.length > 0);
  }, [filtered, stages]);

  // Contract state per deal, fetched once for the board rather than per card.
  const [contracts, setContracts] = useState<Record<string, ContractState>>({});
  useEffect(() => {
    contractsApi.states().then(setContracts).catch(() => setContracts({}));
  }, []);

  const stageFor = (id: string) => stages.find((s) => s.id === id);
  const currency = opps[0]?.currency || 'USD';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Handshake size={20} className="text-accent" />
            Opportunities
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Deals from any origin — inbound, outbound, referral, expansion or RFP.{' '}
            <span className="text-gray-400 dark:text-gray-500">
              An opportunity does not need a lead behind it.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SellingAs />
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white"
          >
            <Plus size={14} /> New opportunity
          </button>
          <button
            onClick={load}
            title="Refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Forecast. Both numbers are open pipeline only — including won and lost
          would make them grow forever and mean nothing. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Open pipeline</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {money(pipelineValue, currency)}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Weighted</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {money(weightedValue, currency)}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Open deals</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {opps.filter((o) => o.status !== 'won' && o.status !== 'lost').length}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Account, contact…"
            className="pl-8 pr-3 py-1.5 w-64 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        {sources.length > 0 && (
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">All sources</option>
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
          {filtered.length} of {opps.length}
        </span>
      </div>

      {opps.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Inbox size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">No opportunities yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            Convert a qualified lead from the Leads view, or create one directly —
            an opportunity does not require a lead.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-10 text-center">
          No opportunities match that filter.
        </p>
      ) : (
        <div className="space-y-6">
          {grouped.map(([stage, rows]) => (
            <section key={stage}>
              <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: stageFor(stage)?.color || '#94a3b8' }}
                />
                {stageFor(stage)?.label || stage}
                <span className="font-normal text-gray-400">{rows.length}</span>
                <span className="ml-auto font-normal normal-case tracking-normal text-gray-400">
                  {money(rows.reduce((n, o) => n + (o.valueMinor || 0), 0), currency)}
                </span>
              </h2>
              <div className="space-y-1.5">
                {rows.map((opp) => (
                  <button
                    key={opp.id}
                    onClick={() => setDetailItemId(opp.id)}
                    className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 hover:border-accent/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {opp.title}
                          </span>
                          <ContractBadge state={contracts[opp.id]} won={opp.status === 'won'} />
                          {opp.account && (
                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                              <Building2 size={11} /> {opp.account}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {opp.contactName && (
                            <span className="flex items-center gap-1">
                              <User size={11} /> {opp.contactName}
                            </span>
                          )}
                          {opp.expectedCloseDate && (
                            <span className="flex items-center gap-1">
                              <CalendarClock size={11} />
                              {new Date(opp.expectedCloseDate).toLocaleDateString()}
                            </span>
                          )}
                          {opp.sourceLeadId && (
                            <span className="text-gray-400 dark:text-gray-500">
                              from a lead
                            </span>
                          )}
                          {/* Who sells it and who it goes through — the two
                              companies whose names the quote carries. */}
                          {opp.sellerOrgId ? (
                            <span className="flex items-center gap-1" title="Selling company">
                              <Building size={11} /> {nameOf(opp.sellerOrgId)}
                            </span>
                          ) : (
                            <span className="text-amber-600 dark:text-amber-500" title="No selling company set — shown under every company">
                              no seller set
                            </span>
                          )}
                          {opp.partnerOrgId && (
                            <span title="Partner (reseller)">
                              via {orgNames[opp.partnerOrgId.toUpperCase()] || opp.partnerOrgId}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {opp.valueMinor
                            ? money(opp.valueMinor, opp.currency)
                            : <span className="text-gray-400 font-normal">no value yet</span>}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {opp.probability}% · {opp.source || 'unknown source'}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {creating && (
        <NewOpportunityModal
          projectIds={projectIds}
          onClose={() => setCreating(false)}
          onCreated={(opp) => { setCreating(false); load(); setDetailItemId(opp.id); }}
        />
      )}
    </div>
  );
}

/**
 * A deal with no lead behind it — outbound, referral, expansion, RFP.
 *
 * The seller comes from the session's "Selling as"; client and partner are
 * chosen, never inferred. Choosing a client proposes that account's standing
 * partner, into an empty partner field only.
 */
function NewOpportunityModal({ projectIds, onClose, onCreated }: {
  projectIds: string[];
  onClose: () => void;
  onCreated: (opp: Opportunity) => void;
}) {
  const sessionSeller = useCrmSellerStore((s) => s.sellerOrgId);
  const sellers = useCrmSellerStore((s) => s.sellers);
  const { projects, fetchProjects } = useProjectStore();
  const [orgs, setOrgs] = useState<CrmClient[]>([]);
  // Leads that can still become an opportunity. Choosing one CONVERTS it —
  // the same action as Convert on the Leads board, so the lead is linked and
  // moves to Converted — rather than creating a deal beside it.
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadId, setLeadId] = useState('');
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState('');
  const [form, setForm] = useState<Record<string, string>>({
    projectId: projectIds[0] || '', title: '', oppSellerOrgId: sessionSeller,
    oppClientId: '', oppPartnerOrgId: '', oppAccount: '', oppContactName: '',
    oppContactEmail: '', oppSource: 'outbound', oppCurrency: 'USD',
    oppExpectedCloseDate: '',
  });

  useEffect(() => {
    crmApi.listClients().then((r) => setOrgs(r.clients || [])).catch(() => setOrgs([]));
    crmApi.listLeads()
      .then((r) => setLeads((r.leads || []).filter(
        (l) => !l.convertedToId && l.status !== 'disqualified')))
      .catch(() => setLeads([]));
    // Only needed when no CRM project is known yet — the first deal anywhere.
    if (!projectIds.length) fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const partners = orgs.filter((o) => o.orgType === 'reseller' || o.orgType === 'parent');
  const nameOf = (orgId?: string) =>
    (orgId ? orgs.find((o) => (o.orgId || '').toUpperCase() === orgId.toUpperCase())?.name || orgId : '');
  const client = orgs.find((o) => o._id === form.oppClientId);

  /** Take the lead's contact across; commercial fields stay as typed. */
  const chooseLead = (id: string) => {
    setLeadId(id);
    const lead = leads.find((l) => l.id === id);
    if (!lead) return;
    setForm((f) => ({
      ...f,
      projectId: lead.projectId || f.projectId,
      title: f.title || lead.organisation || lead.name || lead.title,
      oppContactName: f.oppContactName || lead.name || '',
      oppContactEmail: f.oppContactEmail || lead.email || '',
      oppAccount: f.oppAccount || lead.organisation || '',
      oppSource: 'inbound',
    }));
  };

  const chooseClient = (clientId: string) => {
    const chosen = orgs.find((o) => o._id === clientId);
    setForm((f) => ({
      ...f,
      oppClientId: clientId,
      oppAccount: f.oppAccount || chosen?.name || '',
      oppContactName: f.oppContactName || chosen?.contacts?.[0]?.name || '',
      oppContactEmail: f.oppContactEmail || chosen?.contacts?.[0]?.email || '',
      oppPartnerOrgId: f.oppPartnerOrgId || chosen?.partnerOrgId || '',
    }));
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Give the opportunity a name'); return; }
    if (!form.projectId) { toast.error('Choose the project it belongs to'); return; }
    const parsed = Number(amount.replace(/[^0-9.]/g, ''));
    if (amount && !Number.isFinite(parsed)) { toast.error('Value must be a number'); return; }
    setSaving(true);
    try {
      const fields = {
        ...form,
        // Integer minor units, rounded once at the boundary.
        oppValueMinor: amount ? Math.round(parsed * 100) : 0,
      };
      const opp = leadId
        ? (await crmApi.convertLead(leadId, fields)).opportunity
        : await crmApi.createOpportunity(fields);
      toast.success(leadId ? `Converted to ${opp.displayId}` : `Created ${opp.displayId}`);
      onCreated(opp);
    } catch (err: any) {
      toast.error(err.message || 'Could not create the opportunity', { duration: 9000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">New opportunity</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className={LABEL}>From a lead (optional)</label>
            <select className={FIELD} value={leadId} onChange={(e) => chooseLead(e.target.value)}>
              <option value="">— none: a new deal —</option>
              {leads.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.displayId} — {l.name || l.title}{l.organisation ? ` (${l.organisation})` : ''}
                </option>
              ))}
            </select>
            {leadId && (
              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                The lead is converted: it links to this opportunity and moves to Converted.
              </p>
            )}
          </div>
          <div>
            <label className={LABEL}>Opportunity name</label>
            <input className={FIELD} value={form.title} autoFocus
              onChange={(e) => set('title', e.target.value)} />
          </div>
          {!projectIds.length && !leadId && (
            <div>
              <label className={LABEL}>Project</label>
              <select className={FIELD} value={form.projectId}
                onChange={(e) => set('projectId', e.target.value)}>
                <option value="">— choose —</option>
                {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className={LABEL}>Selling as</label>
            <select className={FIELD} value={form.oppSellerOrgId}
              onChange={(e) => set('oppSellerOrgId', e.target.value)}>
              <option value="">— not set —</option>
              {sellers.map((s) => <option key={s._id} value={s.orgId}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Client (end customer)</label>
              <select className={FIELD} value={form.oppClientId}
                onChange={(e) => chooseClient(e.target.value)}>
                <option value="">— not set —</option>
                {orgs.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}{c.orgId ? ` · ${c.orgId}` : ''}</option>
                ))}
              </select>
              {client?.parentOrgId && (
                <p className="mt-1 text-[11px] text-gray-400">Parent organisation: {nameOf(client.parentOrgId)}</p>
              )}
            </div>
            <div>
              <label className={LABEL}>Partner (reseller)</label>
              <select className={FIELD} value={form.oppPartnerOrgId}
                onChange={(e) => set('oppPartnerOrgId', e.target.value)}>
                <option value="">— not set —</option>
                {partners.map((r) => (
                  <option key={r._id} value={r.orgId || ''}>{r.name}{r.orgId ? ` · ${r.orgId}` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Contact</label>
              <input className={FIELD} value={form.oppContactName}
                onChange={(e) => set('oppContactName', e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Contact email</label>
              <input className={FIELD} type="email" value={form.oppContactEmail}
                onChange={(e) => set('oppContactEmail', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={LABEL}>Estimated value</label>
              <input className={FIELD} inputMode="decimal" placeholder="0.00"
                value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Currency</label>
              <input className={FIELD} value={form.oppCurrency}
                onChange={(e) => set('oppCurrency', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label className={LABEL}>Source</label>
              <select className={FIELD} value={form.oppSource}
                onChange={(e) => set('oppSource', e.target.value)}>
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL}>Expected close date</label>
            <input type="date" className={FIELD} value={form.oppExpectedCloseDate}
              onChange={(e) => set('oppExpectedCloseDate', e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
            {saving ? 'Creating…' : leadId ? 'Convert to opportunity' : 'Create opportunity'}
          </button>
        </div>
      </div>
    </div>
  );
}
